/** Retained-file metadata is authenticated transport state, never part of publicTurn. */
export type SharedAttachment = { sourceEventId: string; available: false } | {
  sourceEventId: string; available: true; fileName: string;
  mediaKind: 'IMAGE' | 'DOCUMENT' | 'VIDEO' | 'AUDIO'; mimeType: string;
  byteCount: number; sha256: string; inspection?: 'NOT_ANALYZED';
};
export type AvailableSharedAttachment = Extract<SharedAttachment, { available: true }>;
export const MAX_SHARED_ATTACHMENT_BYTES = 32 * 1024 * 1024;

export function parseSharedAttachments(value: unknown): SharedAttachment[] {
  if (!Array.isArray(value) || value.length > 4) return [];
  const items = new Map<string, SharedAttachment>();
  for (const item of value) {
    if (!item || typeof item !== 'object' || typeof item.sourceEventId !== 'string'
      || !item.sourceEventId || item.sourceEventId.length > 256) continue;
    const unavailable: SharedAttachment = { sourceEventId: item.sourceEventId, available: false };
    const valid = item.available === true && typeof item.fileName === 'string' && item.fileName.length > 0 && item.fileName.length <= 512
      && ['IMAGE', 'DOCUMENT', 'VIDEO', 'AUDIO'].includes(item.mediaKind)
      && typeof item.mimeType === 'string' && /^[a-z0-9.+-]+\/[a-z0-9.+-]+$/u.test(item.mimeType)
      && Number.isSafeInteger(item.byteCount) && item.byteCount > 0 && item.byteCount <= (item.mimeType === 'application/pdf' ? 64 * 1024 * 1024 : MAX_SHARED_ATTACHMENT_BYTES)
      && typeof item.sha256 === 'string' && /^[a-f0-9]{64}$/u.test(item.sha256);
    // Duplicate/conflicting identities are not permission to choose convenient bytes.
    items.set(item.sourceEventId, items.has(item.sourceEventId) || !valid ? unavailable : {
      sourceEventId: item.sourceEventId, available: true, fileName: item.fileName,
      mediaKind: item.mediaKind, mimeType: item.mimeType, byteCount: item.byteCount, sha256: item.sha256,
      ...(['VIDEO', 'IMAGE'].includes(item.mediaKind) && item.inspection === 'NOT_ANALYZED' ? { inspection: 'NOT_ANALYZED' as const } : {})
    });
  }
  return [...items.values()];
}

export function sharedAttachmentIdentity(item: SharedAttachment): string { return JSON.stringify(item); }
export function sharedAttachmentUrl(item: AvailableSharedAttachment): string {
  return `/api/ops-assistant/sources/${encodeURIComponent(item.sourceEventId)}/file?expectedOriginalSha256=${item.sha256}`;
}
const OFFICE_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
]);
export function isOfficeSharedAttachment(item: AvailableSharedAttachment): boolean {
  return item.mediaKind === 'DOCUMENT' && OFFICE_MIME_TYPES.has(item.mimeType);
}
export function sharedOfficePreviewUrl(item: AvailableSharedAttachment): string {
  return `/api/ops-assistant/sources/${encodeURIComponent(item.sourceEventId)}/preview.pdf?expectedOriginalSha256=${item.sha256}`;
}
export function sharedAttachmentMode(item: AvailableSharedAttachment): 'PDF' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'AUDIO' | null {
  if (isOfficeSharedAttachment(item)) return 'PDF';
  if (item.mediaKind === 'DOCUMENT' && item.mimeType === 'application/pdf') return 'PDF';
  // WHY: intake emits text/md and text/html for supported .md/.html uploads.
  // Both use the literal UTF-8 <pre> viewer; never parse markup or inspection text.
  if (item.mediaKind === 'DOCUMENT' && ['text/plain', 'text/markdown', 'text/md', 'text/html', 'text/csv', 'application/json'].includes(item.mimeType)) return 'TEXT';
  if (item.mediaKind === 'IMAGE' && ['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(item.mimeType)) return 'IMAGE';
  if (item.mediaKind === 'VIDEO' && ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'].includes(item.mimeType)) return 'VIDEO';
  if (item.mediaKind === 'AUDIO' && ['audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/x-wav', 'audio/webm', 'audio/ogg', 'audio/aac', 'audio/flac'].includes(item.mimeType)) return 'AUDIO';
  return null;
}

export function reconcileSharedAttachments(current: readonly SharedAttachment[] | undefined, incoming: readonly SharedAttachment[] | undefined): SharedAttachment[] {
  // WHY: a fresh local row must not shadow authenticated revocation or restore
  // a cached descriptor after reload. A conflicting retained identity fails closed.
  return parseSharedAttachments(incoming).map(item => {
    const previous = current?.find(old => old.sourceEventId === item.sourceEventId);
    return previous?.available && item.available && (previous.sha256 !== item.sha256
      || previous.byteCount !== item.byteCount || previous.mimeType !== item.mimeType || previous.mediaKind !== item.mediaKind)
      ? { sourceEventId: item.sourceEventId, available: false } : item;
  });
}

export async function loadSharedAttachmentBytes(item: AvailableSharedAttachment, signal: AbortSignal): Promise<Uint8Array<ArrayBuffer>> {
  signal.throwIfAborted();
  const checked = parseSharedAttachments([item])[0];
  if (!checked?.available) throw new Error('shared_file_metadata_invalid');
  const response = await fetch(sharedAttachmentUrl(checked), { credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal });
  signal.throwIfAborted();
  return readSharedAttachmentResponse(response, checked, signal);
}

type OfficePreviewJob = { signal: AbortSignal; run: () => Promise<Uint8Array<ArrayBuffer>>; resolve: (bytes: Uint8Array<ArrayBuffer>) => void; reject: (error: unknown) => void; abort: () => void };
const pendingOfficePreviews: OfficePreviewJob[] = [];
let officePreviewBusy = false;
const MAX_PENDING_OFFICE_PREVIEWS = 16;

// WHY: the offline renderer owns one job. Starting every intersecting attachment
// together left all but one card permanently unavailable. Queue only this browser's
// visible Office work, bounded in memory; no server queue, polling or auto-retry.
// Private/scope retirement must remove queued jobs before they can issue a fetch.
function drainOfficePreviews(): void {
  if (officePreviewBusy) return;
  const job = pendingOfficePreviews.shift();
  if (!job) return;
  job.signal.removeEventListener('abort', job.abort);
  if (job.signal.aborted) { job.reject(job.signal.reason); drainOfficePreviews(); return; }
  officePreviewBusy = true;
  void Promise.resolve().then(() => { job.signal.throwIfAborted(); return job.run(); })
    .then(job.resolve, job.reject).finally(() => { officePreviewBusy = false; drainOfficePreviews(); });
}
function scheduleOfficePreview(signal: AbortSignal, run: () => Promise<Uint8Array<ArrayBuffer>>): Promise<Uint8Array<ArrayBuffer>> {
  signal.throwIfAborted();
  if (pendingOfficePreviews.length >= MAX_PENDING_OFFICE_PREVIEWS) return Promise.reject(new Error('office_preview_busy'));
  return new Promise((resolve, reject) => {
    const job: OfficePreviewJob = { signal, run, resolve, reject, abort: () => {
      const index = pendingOfficePreviews.indexOf(job);
      if (index !== -1) pendingOfficePreviews.splice(index, 1);
      reject(signal.reason);
    } };
    signal.addEventListener('abort', job.abort, { once: true });
    pendingOfficePreviews.push(job); drainOfficePreviews();
  });
}

/** Original SHA binds authorization; derivative SHA binds the separately rendered PDF.
 * Never verify a converted preview against the original Office digest or embed Office/HTML.
 */
export async function loadSharedOfficePreviewBytes(item: AvailableSharedAttachment, signal: AbortSignal): Promise<Uint8Array<ArrayBuffer>> {
  return scheduleOfficePreview(signal, () => fetchSharedOfficePreviewBytes(item, signal));
}
async function fetchSharedOfficePreviewBytes(item: AvailableSharedAttachment, signal: AbortSignal): Promise<Uint8Array<ArrayBuffer>> {
  signal.throwIfAborted();
  const checked = parseSharedAttachments([item])[0];
  if (!checked?.available || !isOfficeSharedAttachment(checked)) throw new Error('office_preview_metadata_invalid');
  const response = await fetch(sharedOfficePreviewUrl(checked), { credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal });
  signal.throwIfAborted();
  const length = response.headers.get('content-length') ?? '';
  const sourceSha = response.headers.get('X-Business-Ops-Source-Sha256');
  const previewSha = response.headers.get('X-Business-Ops-Preview-Sha256') ?? '';
  const pages = response.headers.get('X-Business-Ops-Preview-Pages') ?? '';
  const renderer = response.headers.get('X-Business-Ops-Preview-Renderer') ?? '';
  if (!response.ok || sourceSha !== checked.sha256 || !/^[a-f0-9]{64}$/u.test(previewSha)
    || !/^[1-9]\d*$/u.test(length) || Number(length) > 8 * 1024 * 1024
    || !/^[1-9]\d*$/u.test(pages) || Number(pages) > 512
    || !/^sha256:[a-f0-9]{64}$/u.test(renderer)) {
    await response.body?.cancel(); throw new Error('office_preview_unavailable');
  }
  return readSharedAttachmentResponse(response, { byteCount: Number(length), sha256: previewSha, mimeType: 'application/pdf' }, signal);
}

async function readSharedAttachmentResponse(response: Response, checked: { byteCount: number; sha256: string; mimeType: string }, signal: AbortSignal): Promise<Uint8Array<ArrayBuffer>> {
  const declared = response.headers.get('content-length');
  const mime = response.headers.get('content-type')?.split(';', 1)[0]?.trim().toLowerCase();
  if (!response.ok || mime !== checked.mimeType || (declared !== null && Number(declared) !== checked.byteCount)) {
    await response.body?.cancel(); throw new Error('shared_file_unavailable');
  }
  const reader = response.body?.getReader();
  if (!reader) throw new Error('shared_file_empty');
  const bytes = new Uint8Array(checked.byteCount);
  let size = 0; let finished = false;
  const abort = () => { void reader.cancel().catch(() => {}); };
  signal.addEventListener('abort', abort, { once: true });
  try {
    for (;;) {
      signal.throwIfAborted();
      const next = await reader.read();
      signal.throwIfAborted();
      if (next.done) break;
      if (size + next.value.byteLength > bytes.byteLength) throw new Error('shared_file_size_mismatch');
      bytes.set(next.value, size); size += next.value.byteLength;
    }
    if (size !== bytes.byteLength) throw new Error('shared_file_size_mismatch');
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    signal.throwIfAborted();
    if ([...new Uint8Array(digest)].map(n => n.toString(16).padStart(2, '0')).join('') !== checked.sha256) throw new Error('shared_file_integrity_mismatch');
    finished = true; return bytes;
  } finally {
    signal.removeEventListener('abort', abort);
    if (!finished) await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

/** Unanalyzed media retention must never become evidence admission. */
export function sharedUploadSourceIds(receipt: { sourceEventId: string; status: string; mediaKind: string } | null): { sourceEventIds: string[]; presentationSourceEventIds: string[] } {
  if (!receipt) return { sourceEventIds: [], presentationSourceEventIds: [] };
  if (receipt.status === 'READY') return { sourceEventIds: [receipt.sourceEventId], presentationSourceEventIds: [] };
  if (receipt.status === 'RETAINED' && ['VIDEO', 'IMAGE'].includes(receipt.mediaKind)) return { sourceEventIds: [], presentationSourceEventIds: [receipt.sourceEventId] };
  throw new Error('Unsupported retained upload receipt.');
}
